import type { CheckedInVisitor, UpcomingVisitor } from '@/types';

/**
 * Guard offline cache service — IndexedDB-backed gate queue caching.
 *
 * DB: securegate-guard-cache, version 1
 * Object store: gate-queue, keyed by gateId
 * Cache TTL: 30 minutes
 *
 * Requirements: 12.2, 12.4
 */

const DB_NAME = 'securegate-guard-cache';
const DB_VERSION = 1;
const STORE_NAME = 'gate-queue';
export const CACHE_TTL_MS = 30 * 60 * 1000;

export interface CachedGateQueue {
  gateId: string;
  checkedIn: CheckedInVisitor[];
  upcoming: UpcomingVisitor[];
  walkInsOnSite: number;
  cachedAt: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'gateId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function writeGateQueueCache(data: CachedGateQueue): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(data);
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // IndexedDB unavailable
  }
}

export async function readGateQueueCache(gateId: string): Promise<CachedGateQueue | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).get(gateId);
    const result = await new Promise<CachedGateQueue | null>((resolve, reject) => {
      request.onsuccess = () => resolve((request.result as CachedGateQueue) ?? null);
      request.onerror = () => reject(request.error);
    });
    db.close();
    return result;
  } catch {
    return null;
  }
}

export function isCacheStale(cached: CachedGateQueue): boolean {
  return Date.now() - cached.cachedAt > CACHE_TTL_MS;
}

export async function clearGateQueueCache(): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // IndexedDB unavailable
  }
}
